/**
 * Core types for chat-sdk
 */

import type { Root } from "mdast";
import type { CardElement } from "./cards";
import type { CardJSXElement } from "./jsx-runtime";
import type { Logger, LogLevel } from "./logger";
import type { Message } from "./message";
import type { ModalElement } from "./modals";

// =============================================================================
// Re-exports from extracted modules
// =============================================================================

export {
  ChatError,
  LockError,
  NotImplementedError,
  RateLimitError,
} from "./errors";
export type { Logger, LogLevel } from "./logger";
export { ConsoleLogger } from "./logger";

// =============================================================================
// Configuration
// =============================================================================

/**
 * Chat configuration with type-safe adapter inference.
 * @template TAdapters - Record of adapter name to adapter instance
 */
export interface ChatConfig<
  TAdapters extends Record<string, Adapter> = Record<string, Adapter>,
> {
  /** Default bot username across all adapters */
  userName: string;
  /** Map of adapter name to adapter instance */
  adapters: TAdapters;
  /** State adapter for subscriptions and locking */
  state: StateAdapter;
  /**
   * Logger instance or log level.
   * Pass "silent" to disable all logging.
   */
  logger: Logger | LogLevel;
  /**
   * Update interval for fallback streaming (post + edit) in milliseconds.
   * Defaults to 500ms. Lower values provide smoother updates but may hit rate limits.
   */
  streamingUpdateIntervalMs?: number;
}

/**
 * Options for webhook handling.
 */
export interface WebhookOptions {
  /**
   * Function to run message handling in the background.
   * Use this to ensure fast webhook responses while processing continues.
   *
   * @example
   * // Next.js App Router
   * import { after } from "next/server";
   * chat.webhooks.slack(request, { waitUntil: (p) => after(() => p) });
   *
   * @example
   * // Vercel Functions
   * import { waitUntil } from "@vercel/functions";
   * chat.webhooks.slack(request, { waitUntil });
   */
  waitUntil?: (task: Promise<unknown>) => void;
}

// =============================================================================
// Adapter Interface
// =============================================================================

/**
 * Adapter interface with generics for platform-specific types.
 * @template TThreadId - Platform-specific thread ID data type
 * @template TRawMessage - Platform-specific raw message type
 */
export interface Adapter<TThreadId = unknown, TRawMessage = unknown> {
  /** Unique name for this adapter (e.g., "slack", "teams") */
  readonly name: string;
  /** Bot username (can override global userName) */
  readonly userName: string;
  /** Bot user ID for platforms that use IDs in mentions (e.g., Slack's <@U123>) */
  readonly botUserId?: string;

  /** Called when Chat instance is created (internal use) */
  initialize(chat: ChatInstance): Promise<void>;

  /** Handle incoming webhook request */
  handleWebhook(request: Request, options?: WebhookOptions): Promise<Response>;

  /** Post a message to a thread */
  postMessage(
    threadId: string,
    message: AdapterPostableMessage,
  ): Promise<RawMessage<TRawMessage>>;

  /** Edit an existing message */
  editMessage(
    threadId: string,
    messageId: string,
    message: AdapterPostableMessage,
  ): Promise<RawMessage<TRawMessage>>;

  /** Delete a message */
  deleteMessage(threadId: string, messageId: string): Promise<void>;

  /** Add a reaction to a message */
  addReaction(
    threadId: string,
    messageId: string,
    emoji: EmojiValue | string,
  ): Promise<void>;

  /** Remove a reaction from a message */
  removeReaction(
    threadId: string,
    messageId: string,
    emoji: EmojiValue | string,
  ): Promise<void>;

  /** Show typing indicator */
  startTyping(threadId: string): Promise<void>;

  /**
   * Fetch messages from a thread.
   *
   * **Direction behavior:**
   * - `backward` (default): Fetches the most recent messages. Use this for loading
   *   a chat view. The `nextCursor` points to older messages.
   * - `forward`: Fetches the oldest messages first. Use this for iterating through
   *   message history. The `nextCursor` points to newer messages.
   *
   * **Message ordering:**
   * Messages within each page are always returned in chronological order (oldest first),
   * regardless of direction. This is the natural reading order for chat messages.
   *
   * @example
   * ```typescript
   * // Load most recent 50 messages for display
   * const recent = await adapter.fetchMessages(threadId, { limit: 50 });
   * // recent.messages: [older, ..., newest] in chronological order
   *
   * // Paginate backward to load older messages
   * const older = await adapter.fetchMessages(threadId, {
   *   limit: 50,
   *   cursor: recent.nextCursor,
   * });
   *
   * // Iterate through all history from the beginning
   * const history = await adapter.fetchMessages(threadId, {
   *   limit: 100,
   *   direction: 'forward',
   * });
   * ```
   */
  fetchMessages(
    threadId: string,
    options?: FetchOptions,
  ): Promise<FetchResult<TRawMessage>>;

  /** Fetch thread metadata */
  fetchThread(threadId: string): Promise<ThreadInfo>;

  /** Encode platform-specific data into a thread ID string */
  encodeThreadId(platformData: TThreadId): string;

  /** Decode thread ID string back to platform-specific data */
  decodeThreadId(threadId: string): TThreadId;

  /** Parse platform message format to normalized format */
  parseMessage(raw: TRawMessage): Message<TRawMessage>;

  /** Render formatted content to platform-specific string */
  renderFormatted(content: FormattedContent): string;

  /**
   * Optional hook called when a thread is subscribed to.
   * Adapters can use this to set up platform-specific subscriptions
   * (e.g., Google Chat Workspace Events).
   */
  onThreadSubscribe?(threadId: string): Promise<void>;

  /**
   * Open a direct message conversation with a user.
   *
   * @param userId - The platform-specific user ID
   * @returns The thread ID for the DM conversation
   *
   * @example
   * ```typescript
   * const dmThreadId = await adapter.openDM("U123456");
   * await adapter.postMessage(dmThreadId, "Hello!");
   * ```
   */
  openDM?(userId: string): Promise<string>;

  /**
   * Check if a thread is a direct message conversation.
   *
   * @param threadId - The thread ID to check
   * @returns True if the thread is a DM, false otherwise
   */
  isDM?(threadId: string): boolean;

  /**
   * Open a modal/dialog form.
   *
   * @param triggerId - Platform-specific trigger ID from the action event
   * @param modal - The modal element to display
   * @returns The view/dialog ID
   */
  openModal?(
    triggerId: string,
    modal: ModalElement,
  ): Promise<{ viewId: string }>;

  /**
   * Stream a message using platform-native streaming APIs.
   *
   * The adapter consumes the async iterable and handles the entire streaming lifecycle.
   * Only available on platforms with native streaming support (e.g., Slack).
   *
   * @param threadId - The thread to stream to
   * @param textStream - Async iterable of text chunks (e.g., from AI SDK)
   * @param options - Platform-specific streaming options
   * @returns The raw message after streaming completes
   */
  stream?(
    threadId: string,
    textStream: AsyncIterable<string>,
    options?: StreamOptions,
  ): Promise<RawMessage<TRawMessage>>;
}

/**
 * Options for streaming messages.
 * Platform-specific options are passed through to the adapter.
 */
export interface StreamOptions {
  /** Slack: The user ID to stream to (for AI assistant context) */
  recipientUserId?: string;
  /** Slack: The team/workspace ID */
  recipientTeamId?: string;
  /** Minimum interval between updates in ms (default: 1000). Used for fallback mode (GChat/Teams). */
  updateIntervalMs?: number;
}

/** Internal interface for Chat instance passed to adapters */
export interface ChatInstance {
  /**
   * Process an incoming message from an adapter.
   * Handles waitUntil registration and error catching internally.
   *
   * @param adapter - The adapter that received the message
   * @param threadId - The thread ID
   * @param message - Either a parsed message, or a factory function for lazy async parsing
   * @param options - Webhook options including waitUntil
   */
  processMessage(
    adapter: Adapter,
    threadId: string,
    message: Message | (() => Promise<Message>),
    options?: WebhookOptions,
  ): void;

  /**
   * @deprecated Use processMessage instead. This method is for internal use.
   */
  handleIncomingMessage(
    adapter: Adapter,
    threadId: string,
    message: Message,
  ): Promise<void>;

  /**
   * Process an incoming reaction event from an adapter.
   * Handles waitUntil registration and error catching internally.
   *
   * @param event - The reaction event (without adapter field, will be added)
   * @param options - Webhook options including waitUntil
   */
  processReaction(
    event: Omit<ReactionEvent, "adapter" | "thread"> & { adapter?: Adapter },
    options?: WebhookOptions,
  ): void;

  /**
   * Process an incoming action event (button click) from an adapter.
   * Handles waitUntil registration and error catching internally.
   *
   * @param event - The action event (without thread field, will be added)
   * @param options - Webhook options including waitUntil
   */
  processAction(
    event: Omit<ActionEvent, "thread" | "openModal"> & { adapter: Adapter },
    options?: WebhookOptions,
  ): void;

  processModalSubmit(
    event: Omit<ModalSubmitEvent, "thread">,
    options?: WebhookOptions,
  ): Promise<ModalResponse | undefined>;

  processModalClose(
    event: Omit<ModalCloseEvent, "thread">,
    options?: WebhookOptions,
  ): void;

  getState(): StateAdapter;
  getUserName(): string;
  /** Get the configured logger, optionally with a child prefix */
  getLogger(prefix?: string): Logger;
}

// =============================================================================
// State Adapter Interface
// =============================================================================

export interface StateAdapter {
  /** Connect to the state backend */
  connect(): Promise<void>;

  /** Disconnect from the state backend */
  disconnect(): Promise<void>;

  /** Subscribe to a thread (persists across restarts) */
  subscribe(threadId: string): Promise<void>;

  /** Unsubscribe from a thread */
  unsubscribe(threadId: string): Promise<void>;

  /** Check if subscribed to a thread */
  isSubscribed(threadId: string): Promise<boolean>;

  /** List all subscriptions, optionally filtered by adapter */
  listSubscriptions(adapterName?: string): AsyncIterable<string>;

  /** Acquire a lock on a thread (returns null if already locked) */
  acquireLock(threadId: string, ttlMs: number): Promise<Lock | null>;

  /** Release a lock */
  releaseLock(lock: Lock): Promise<void>;

  /** Extend a lock's TTL */
  extendLock(lock: Lock, ttlMs: number): Promise<boolean>;

  /** Get a cached value by key */
  get<T = unknown>(key: string): Promise<T | null>;

  /** Set a cached value with optional TTL in milliseconds */
  set<T = unknown>(key: string, value: T, ttlMs?: number): Promise<void>;

  /** Delete a cached value */
  delete(key: string): Promise<void>;
}

export interface Lock {
  threadId: string;
  token: string;
  expiresAt: number;
}

// =============================================================================
// Thread
// =============================================================================

/** Default TTL for thread state (30 days in milliseconds) */
export const THREAD_STATE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Thread interface with support for custom state.
 * @template TState - Custom state type stored per-thread (default: Record<string, unknown>)
 * @template TRawMessage - Platform-specific raw message type
 */
export interface Thread<
  TState = Record<string, unknown>,
  TRawMessage = unknown,
> {
  /** Unique thread ID (format: "adapter:channel:thread") */
  readonly id: string;
  /** The adapter this thread belongs to */
  readonly adapter: Adapter;
  /** Channel/conversation ID */
  readonly channelId: string;
  /** Whether this is a direct message conversation */
  readonly isDM: boolean;

  /**
   * Get the current thread state.
   * Returns null if no state has been set.
   *
   * @example
   * ```typescript
   * const state = await thread.state;
   * if (state?.aiMode) {
   *   // AI mode is enabled
   * }
   * ```
   */
  readonly state: Promise<TState | null>;

  /**
   * Set the thread state. Merges with existing state by default.
   * State is persisted for 30 days.
   *
   * @param state - Partial state to merge, or full state if replace is true
   * @param options - Options for setting state
   *
   * @example
   * ```typescript
   * // Merge with existing state
   * await thread.setState({ aiMode: true });
   *
   * // Replace entire state
   * await thread.setState({ aiMode: true }, { replace: true });
   * ```
   */
  setState(
    state: Partial<TState>,
    options?: { replace?: boolean },
  ): Promise<void>;

  /** Recently fetched messages (cached) */
  recentMessages: Message<TRawMessage>[];

  /**
   * Async iterator for all messages in the thread.
   * Messages are yielded in chronological order (oldest first).
   * Automatically handles pagination.
   */
  allMessages: AsyncIterable<Message<TRawMessage>>;

  /**
   * Check if this thread is currently subscribed.
   *
   * In subscribed message handlers, this is optimized to return true immediately
   * without a state lookup, since we already know we're in a subscribed context.
   *
   * @returns Promise resolving to true if subscribed, false otherwise
   */
  isSubscribed(): Promise<boolean>;

  /**
   * Subscribe to future messages in this thread.
   *
   * Once subscribed, all messages in this thread will trigger `onSubscribedMessage` handlers.
   * The initial message that triggered subscription will NOT fire the handler.
   *
   * @example
   * ```typescript
   * chat.onNewMention(async (thread, message) => {
   *   await thread.subscribe();  // Subscribe to follow-up messages
   *   await thread.post("I'm now watching this thread!");
   * });
   * ```
   */
  subscribe(): Promise<void>;

  /**
   * Unsubscribe from this thread.
   *
   * Future messages will no longer trigger `onSubscribedMessage` handlers.
   */
  unsubscribe(): Promise<void>;

  /**
   * Post a message to this thread.
   *
   * Supports text, markdown, cards, and streaming from async iterables.
   * When posting a stream (e.g., from AI SDK), uses platform-native streaming
   * APIs when available (Slack), or falls back to post + edit with throttling.
   *
   * @param message - String, PostableMessage, JSX Card, or AsyncIterable<string>
   * @returns A SentMessage with methods to edit, delete, or add reactions
   *
   * @example
   * ```typescript
   * // Simple string
   * await thread.post("Hello!");
   *
   * // Markdown
   * await thread.post({ markdown: "**Bold** and _italic_" });
   *
   * // With emoji
   * await thread.post(`${emoji.thumbs_up} Great job!`);
   *
   * // JSX Card (with @jsxImportSource chat)
   * await thread.post(
   *   <Card title="Welcome!">
   *     <Text>Hello world</Text>
   *   </Card>
   * );
   *
   * // Stream from AI SDK
   * const result = await agent.stream({ prompt: message.text });
   * await thread.post(result.textStream);
   * ```
   */
  post(
    message: string | PostableMessage | CardJSXElement,
  ): Promise<SentMessage<TRawMessage>>;

  /**
   * Show typing indicator in the thread.
   *
   * Some platforms support persistent typing indicators, others just send once.
   */
  startTyping(): Promise<void>;

  /**
   * Refresh `recentMessages` from the API.
   *
   * Fetches the latest 50 messages and updates `recentMessages`.
   */
  refresh(): Promise<void>;

  /**
   * Get a platform-specific mention string for a user.
   * Use this to @-mention a user in a message.
   * @example
   * await thread.post(`Hey ${thread.mentionUser(userId)}, check this out!`);
   */
  mentionUser(userId: string): string;
}

export interface ThreadInfo {
  id: string;
  channelId: string;
  channelName?: string;
  /** Whether this is a direct message conversation */
  isDM?: boolean;
  /** Platform-specific metadata */
  metadata: Record<string, unknown>;
}

/**
 * Direction for fetching messages.
 *
 * - `backward`: Fetch most recent messages first. Pagination moves toward older messages.
 *   This is the default, suitable for loading a chat view (show latest messages first).
 *
 * - `forward`: Fetch oldest messages first. Pagination moves toward newer messages.
 *   Suitable for iterating through message history from the beginning.
 *
 * In both directions, messages within each page are returned in chronological order
 * (oldest first), which is the natural reading order for chat messages.
 *
 * @example
 * ```typescript
 * // Load most recent 50 messages (default)
 * const recent = await adapter.fetchMessages(threadId, { limit: 50 });
 * // recent.messages: [older, ..., newest] (chronological within page)
 * // recent.nextCursor: points to older messages
 *
 * // Iterate through all history from beginning
 * const history = await adapter.fetchMessages(threadId, {
 *   limit: 50,
 *   direction: 'forward',
 * });
 * // history.messages: [oldest, ..., newer] (chronological within page)
 * // history.nextCursor: points to even newer messages
 * ```
 */
export type FetchDirection = "forward" | "backward";

/**
 * Options for fetching messages from a thread.
 */
export interface FetchOptions {
  /** Maximum number of messages to fetch. Default varies by adapter (50-100). */
  limit?: number;
  /**
   * Pagination cursor for fetching the next page of messages.
   * Pass the `nextCursor` from a previous `FetchResult`.
   */
  cursor?: string;
  /**
   * Direction to fetch messages.
   *
   * - `backward` (default): Fetch most recent messages. Cursor moves to older messages.
   * - `forward`: Fetch oldest messages. Cursor moves to newer messages.
   *
   * Messages within each page are always returned in chronological order (oldest first).
   */
  direction?: FetchDirection;
}

/**
 * Result of fetching messages from a thread.
 */
export interface FetchResult<TRawMessage = unknown> {
  /**
   * Messages in chronological order (oldest first within this page).
   *
   * For `direction: 'backward'` (default): These are the N most recent messages.
   * For `direction: 'forward'`: These are the N oldest messages (or next N after cursor).
   */
  messages: Message<TRawMessage>[];
  /**
   * Cursor for fetching the next page.
   * Pass this as `cursor` in the next `fetchMessages` call.
   *
   * - For `direction: 'backward'`: Points to older messages.
   * - For `direction: 'forward'`: Points to newer messages.
   *
   * Undefined if there are no more messages in that direction.
   */
  nextCursor?: string;
}

// =============================================================================
// Message
// =============================================================================

/**
 * Formatted content using mdast AST.
 * This is the canonical representation of message formatting.
 */
export type FormattedContent = Root;

/** Raw message returned from adapter (before wrapping as SentMessage) */
export interface RawMessage<TRawMessage = unknown> {
  id: string;
  threadId: string;
  raw: TRawMessage;
}

export interface Author {
  /** Unique user ID */
  userId: string;
  /** Username/handle for @-mentions */
  userName: string;
  /** Display name */
  fullName: string;
  /** Whether the author is a bot */
  isBot: boolean | "unknown";
  /** Whether the author is this bot */
  isMe: boolean;
}

export interface MessageMetadata {
  /** When the message was sent */
  dateSent: Date;
  /** Whether the message has been edited */
  edited: boolean;
  /** When the message was last edited */
  editedAt?: Date;
}

// =============================================================================
// Sent Message (returned from thread.post())
// =============================================================================

export interface SentMessage<TRawMessage = unknown>
  extends Message<TRawMessage> {
  /** Edit this message with text, a PostableMessage, or a JSX Card element */
  edit(
    newContent: string | PostableMessage | CardJSXElement,
  ): Promise<SentMessage<TRawMessage>>;
  /** Delete this message */
  delete(): Promise<void>;
  /** Add a reaction to this message */
  addReaction(emoji: EmojiValue | string): Promise<void>;
  /** Remove a reaction from this message */
  removeReaction(emoji: EmojiValue | string): Promise<void>;
}

// =============================================================================
// Postable Message
// =============================================================================

/**
 * Input type for adapter postMessage/editMessage methods.
 * This excludes streams since adapters handle content synchronously.
 */
export type AdapterPostableMessage =
  | string
  | PostableRaw
  | PostableMarkdown
  | PostableAst
  | PostableCard
  | CardElement;

/**
 * A message that can be posted to a thread.
 *
 * - `string` - Raw text, passed through as-is to the platform
 * - `{ raw: string }` - Explicit raw text, passed through as-is
 * - `{ markdown: string }` - Markdown text, converted to platform format
 * - `{ ast: Root }` - mdast AST, converted to platform format
 * - `{ card: CardElement }` - Rich card with buttons (Block Kit / Adaptive Cards / GChat Cards)
 * - `CardElement` - Direct card element
 * - `AsyncIterable<string>` - Streaming text (e.g., from AI SDK's textStream)
 */
export type PostableMessage = AdapterPostableMessage | AsyncIterable<string>;

export interface PostableRaw {
  /** Raw text passed through as-is to the platform */
  raw: string;
  /** File/image attachments */
  attachments?: Attachment[];
  /** Files to upload */
  files?: FileUpload[];
}

export interface PostableMarkdown {
  /** Markdown text, converted to platform format */
  markdown: string;
  /** File/image attachments */
  attachments?: Attachment[];
  /** Files to upload */
  files?: FileUpload[];
}

export interface PostableAst {
  /** mdast AST, converted to platform format */
  ast: Root;
  /** File/image attachments */
  attachments?: Attachment[];
  /** Files to upload */
  files?: FileUpload[];
}

export interface PostableCard {
  /** Rich card element */
  card: CardElement;
  /** Fallback text for platforms/clients that can't render cards */
  fallbackText?: string;
  /** Files to upload */
  files?: FileUpload[];
}

export interface Attachment {
  /** Type of attachment */
  type: "image" | "file" | "video" | "audio";
  /** URL to the file (for linking/downloading) */
  url?: string;
  /** Binary data (for uploading or if already fetched) */
  data?: Buffer | Blob;
  /** Filename */
  name?: string;
  /** MIME type */
  mimeType?: string;
  /** File size in bytes */
  size?: number;
  /** Image/video width (if applicable) */
  width?: number;
  /** Image/video height (if applicable) */
  height?: number;
  /**
   * Fetch the attachment data.
   * For platforms that require authentication (like Slack private URLs),
   * this method handles the auth automatically.
   */
  fetchData?: () => Promise<Buffer>;
}

/**
 * File to upload with a message.
 */
export interface FileUpload {
  /** Binary data */
  data: Buffer | Blob | ArrayBuffer;
  /** Filename */
  filename: string;
  /** MIME type (optional, will be inferred from filename if not provided) */
  mimeType?: string;
}

// =============================================================================
// Event Handlers
// =============================================================================

/**
 * Handler for new @-mentions of the bot.
 *
 * **Important**: This handler is ONLY called for mentions in **unsubscribed** threads.
 * Once a thread is subscribed (via `thread.subscribe()`), subsequent messages
 * including @-mentions go to `onSubscribedMessage` handlers instead.
 *
 * To detect mentions in subscribed threads, check `message.isMention`:
 *
 * @example
 * ```typescript
 * // Handle new mentions (unsubscribed threads only)
 * chat.onNewMention(async (thread, message) => {
 *   await thread.subscribe();  // Subscribe to follow-up messages
 *   await thread.post("Hello! I'll be watching this thread.");
 * });
 *
 * // Handle all messages in subscribed threads
 * chat.onSubscribedMessage(async (thread, message) => {
 *   if (message.isMention) {
 *     // User @-mentioned us in a thread we're already watching
 *     await thread.post("You mentioned me again!");
 *   }
 * });
 * ```
 */
export type MentionHandler<TState = Record<string, unknown>> = (
  thread: Thread<TState>,
  message: Message,
) => Promise<void>;

/**
 * Handler for messages matching a regex pattern.
 *
 * Registered via `chat.onNewMessage(pattern, handler)`. Called when a message
 * matches the pattern in an unsubscribed thread.
 */
export type MessageHandler<TState = Record<string, unknown>> = (
  thread: Thread<TState>,
  message: Message,
) => Promise<void>;

/**
 * Handler for messages in subscribed threads.
 *
 * Called for all messages in threads that have been subscribed via `thread.subscribe()`.
 * This includes:
 * - Follow-up messages from users
 * - Messages that @-mention the bot (check `message.isMention`)
 *
 * Does NOT fire for:
 * - The message that triggered the subscription (e.g., the initial @mention)
 * - Messages sent by the bot itself
 *
 * @example
 * ```typescript
 * chat.onSubscribedMessage(async (thread, message) => {
 *   // Handle all follow-up messages
 *   if (message.isMention) {
 *     // User @-mentioned us in a subscribed thread
 *   }
 *   await thread.post(`Got your message: ${message.text}`);
 * });
 * ```
 */
export type SubscribedMessageHandler<TState = Record<string, unknown>> = (
  thread: Thread<TState>,
  message: Message,
) => Promise<void>;

// =============================================================================
// Reactions / Emoji
// =============================================================================

/**
 * Well-known emoji that work across platforms (Slack and Google Chat).
 * These are normalized to a common format regardless of platform.
 */
export type WellKnownEmoji =
  // Reactions & Gestures
  | "thumbs_up"
  | "thumbs_down"
  | "clap"
  | "wave"
  | "pray"
  | "muscle"
  | "ok_hand"
  | "point_up"
  | "point_down"
  | "point_left"
  | "point_right"
  | "raised_hands"
  | "shrug"
  | "facepalm"
  // Emotions & Faces
  | "heart"
  | "smile"
  | "laugh"
  | "thinking"
  | "sad"
  | "cry"
  | "angry"
  | "love_eyes"
  | "cool"
  | "wink"
  | "surprised"
  | "worried"
  | "confused"
  | "neutral"
  | "sleeping"
  | "sick"
  | "mind_blown"
  | "relieved"
  | "grimace"
  | "rolling_eyes"
  | "hug"
  | "zany"
  // Status & Symbols
  | "check"
  | "x"
  | "question"
  | "exclamation"
  | "warning"
  | "stop"
  | "info"
  | "100"
  | "fire"
  | "star"
  | "sparkles"
  | "lightning"
  | "boom"
  | "eyes"
  // Status Indicators
  | "green_circle"
  | "yellow_circle"
  | "red_circle"
  | "blue_circle"
  | "white_circle"
  | "black_circle"
  // Objects & Tools
  | "rocket"
  | "party"
  | "confetti"
  | "balloon"
  | "gift"
  | "trophy"
  | "medal"
  | "lightbulb"
  | "gear"
  | "wrench"
  | "hammer"
  | "bug"
  | "link"
  | "lock"
  | "unlock"
  | "key"
  | "pin"
  | "memo"
  | "clipboard"
  | "calendar"
  | "clock"
  | "hourglass"
  | "bell"
  | "megaphone"
  | "speech_bubble"
  | "email"
  | "inbox"
  | "outbox"
  | "package"
  | "folder"
  | "file"
  | "chart_up"
  | "chart_down"
  | "coffee"
  | "pizza"
  | "beer"
  // Arrows & Directions
  | "arrow_up"
  | "arrow_down"
  | "arrow_left"
  | "arrow_right"
  | "refresh"
  // Nature & Weather
  | "sun"
  | "cloud"
  | "rain"
  | "snow"
  | "rainbow";

/**
 * Platform-specific emoji formats for a single emoji.
 */
export interface EmojiFormats {
  /** Slack emoji name (without colons), e.g., "+1", "heart" */
  slack: string | string[];
  /** Google Chat unicode emoji, e.g., "👍", "❤️" */
  gchat: string | string[];
}

/**
 * Emoji map type - can be extended by users to add custom emoji.
 *
 * @example
 * ```typescript
 * // Extend with custom emoji
 * declare module "chat" {
 *   interface CustomEmojiMap {
 *     "custom_emoji": EmojiFormats;
 *   }
 * }
 *
 * const myEmojiMap: EmojiMapConfig = {
 *   custom_emoji: { slack: "custom", gchat: "🎯" },
 * };
 * ```
 */
// biome-ignore lint/suspicious/noEmptyInterface: Required for TypeScript module augmentation
export interface CustomEmojiMap {}

/**
 * Full emoji type including well-known and custom emoji.
 */
export type Emoji = WellKnownEmoji | keyof CustomEmojiMap;

/**
 * Configuration for emoji mapping.
 */
export type EmojiMapConfig = Partial<Record<Emoji, EmojiFormats>>;

/**
 * Immutable emoji value object with object identity.
 *
 * These objects are singletons - the same emoji name always returns
 * the same frozen object instance, enabling `===` comparison.
 *
 * @example
 * ```typescript
 * // Object identity comparison works
 * if (event.emoji === emoji.thumbs_up) {
 *   console.log("User gave a thumbs up!");
 * }
 *
 * // Works in template strings via toString()
 * await thread.post(`${emoji.thumbs_up} Great job!`);
 * ```
 */
export interface EmojiValue {
  /** The normalized emoji name (e.g., "thumbs_up") */
  readonly name: string;
  /** Returns the placeholder string for message formatting */
  toString(): string;
  /** Returns the placeholder string (for JSON.stringify) */
  toJSON(): string;
}

/**
 * Reaction event fired when a user adds or removes a reaction.
 */
export interface ReactionEvent<TRawMessage = unknown> {
  /** The normalized emoji as an EmojiValue singleton (enables `===` comparison) */
  emoji: EmojiValue;
  /** The raw platform-specific emoji (e.g., "+1" for Slack, "👍" for GChat) */
  rawEmoji: string;
  /** Whether the reaction was added (true) or removed (false) */
  added: boolean;
  /** The user who added/removed the reaction */
  user: Author;
  /** The message that was reacted to (if available) */
  message?: Message<TRawMessage>;
  /** The message ID that was reacted to */
  messageId: string;
  /** The thread ID */
  threadId: string;
  /**
   * The thread where the reaction occurred.
   * Use this to post replies or check subscription status.
   *
   * @example
   * ```typescript
   * chat.onReaction([emoji.thumbs_up], async (event) => {
   *   await event.thread.post(`Thanks for the ${event.emoji}!`);
   * });
   * ```
   */
  thread: Thread<TRawMessage>;
  /** The adapter that received the event */
  adapter: Adapter;
  /** Platform-specific raw event data */
  raw: unknown;
}

/**
 * Handler for reaction events.
 *
 * @example
 * ```typescript
 * // Handle specific emoji
 * chat.onReaction(["thumbs_up", "heart"], async (event) => {
 *   console.log(`${event.user.userName} ${event.added ? "added" : "removed"} ${event.emoji}`);
 * });
 *
 * // Handle all reactions
 * chat.onReaction(async (event) => {
 *   // ...
 * });
 * ```
 */
export type ReactionHandler = (event: ReactionEvent) => Promise<void>;

// =============================================================================
// Action Events (Button Clicks)
// =============================================================================

/**
 * Action event fired when a user clicks a button in a card.
 *
 * @example
 * ```typescript
 * chat.onAction("approve", async (event) => {
 *   await event.thread.post(`Order ${event.value} approved by ${event.user.userName}`);
 * });
 * ```
 */
export interface ActionEvent<TRawMessage = unknown> {
  /** The action ID from the button (matches Button's `id` prop) */
  actionId: string;
  /** Optional value/payload from the button */
  value?: string;
  /** User who clicked the button */
  user: Author;
  /** The thread where the action occurred */
  thread: Thread<TRawMessage>;
  /** The message ID containing the card */
  messageId: string;
  /** The thread ID */
  threadId: string;
  /** The adapter that received the event */
  adapter: Adapter;
  /** Platform-specific raw event data */
  raw: unknown;
  /** Trigger ID for opening modals (required by some platforms, may expire quickly) */
  triggerId?: string;
  /**
   * Open a modal/dialog form in response to this action.
   *
   * @param modal - The modal element to display (JSX or ModalElement)
   * @returns The view/dialog ID, or undefined if modals are not supported
   */
  openModal(
    modal: ModalElement | CardJSXElement,
  ): Promise<{ viewId: string } | undefined>;
}

/**
 * Handler for action events (button clicks in cards).
 *
 * @example
 * ```typescript
 * // Handle specific action
 * chat.onAction("approve", async (event) => {
 *   await event.thread.post("Approved!");
 * });
 *
 * // Handle multiple actions
 * chat.onAction(["approve", "reject"], async (event) => {
 *   if (event.actionId === "approve") {
 *     // ...
 *   }
 * });
 *
 * // Handle all actions (catch-all)
 * chat.onAction(async (event) => {
 *   console.log(`Action: ${event.actionId}`);
 * });
 * ```
 */
export type ActionHandler = (event: ActionEvent) => Promise<void>;

// =============================================================================
// Modal Events (Form Submissions)
// =============================================================================

export interface ModalSubmitEvent {
  callbackId: string;
  viewId: string;
  values: Record<string, string>;
  privateMetadata?: string;
  user: Author;
  adapter: Adapter;
  raw: unknown;
}

export interface ModalCloseEvent {
  callbackId: string;
  viewId: string;
  privateMetadata?: string;
  user: Author;
  adapter: Adapter;
  raw: unknown;
}

export type ModalErrorsResponse = {
  action: "errors";
  errors: Record<string, string>;
};

export type ModalUpdateResponse = {
  action: "update";
  modal: import("./modals").ModalElement;
};

export type ModalPushResponse = {
  action: "push";
  modal: import("./modals").ModalElement;
};

export type ModalCloseResponse = {
  action: "close";
};

export type ModalResponse =
  | ModalCloseResponse
  | ModalErrorsResponse
  | ModalUpdateResponse
  | ModalPushResponse;

export type ModalSubmitHandler = (
  event: ModalSubmitEvent,
) => Promise<ModalResponse | undefined>;

export type ModalCloseHandler = (event: ModalCloseEvent) => Promise<void>;
